import type { Summary } from './types';

export function byId<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`Missing element with id ${id}`);
  }
  return el as T;
}

export function normalizeNumber(value: number | string | undefined): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return value;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? undefined : parsed;
}

export function normalizeNumberArray(input?: Array<number | string>): number[] | undefined {
  if (!Array.isArray(input)) return undefined;
  const normalized = input
    .map((value) => (typeof value === 'number' ? value : Number(value)))
    .filter((value) => !Number.isNaN(value));
  return normalized.length ? normalized : undefined;
}

export function compareQuestionIds(a: string, b: string): number {
  const pattern = /^([A-Za-z]*)(\d+)$/;
  const matchA = a.match(pattern);
  const matchB = b.match(pattern);
  if (matchA && matchB) {
    const prefixCompare = (matchA[1] ?? '').localeCompare(matchB[1] ?? '');
    if (prefixCompare !== 0) return prefixCompare;
    return Number(matchA[2]) - Number(matchB[2]);
  }
  if (matchA) return -1;
  if (matchB) return 1;
  return a.localeCompare(b);
}

export function formatPercentValue(value: number): string {
  if (!Number.isFinite(value)) return '0%';
  const rounded = Math.round(value * 10) / 10;
  return `${rounded.toFixed(1)}%`;
}

export function getBarOrientation(chartHint?: string): 'vertical' | 'horizontal' {
  const hint = chartHint?.trim().toLowerCase();
  if (!hint) return 'vertical';
  if (hint === 'bar-horizontal' || hint === 'horizontal-bar' || hint === 'horizontal' || hint === 'hbar') {
    return 'horizontal';
  }
  return 'vertical';
}

export interface BarDatum {
  label: string;
  count: number;
  percentage: number;
}

export function buildBarData(summary: Summary): BarDatum[] {
  if (!summary.labels || !summary.counts) return [];
  const data = summary.labels.map((label, index) => {
    const count = summary.counts?.[index] ?? 0;
    const percentage = summary.n ? (count / summary.n) * 100 : 0;
    return { label, count, percentage } as BarDatum;
  });
  if (!summary.ordered) {
    data.sort((a, b) => b.count - a.count);
  }
  return data;
}

